/**
 * Pricing validation and utility functions
 * Used across trade components to ensure consistent pricing handling
 */

export interface PricingData {
  retailPrice: number
  currency: string
  offerPrice?: number
  isParsed?: boolean
}

export interface DataDetectedPricing {
  price: number
  currency: string
  title?: string
  image?: string
  [key: string]: any
}

// Import currency symbol map for comprehensive currency support
import getSymbolFromCurrency from 'currency-symbol-map'

// Comprehensive list of supported currencies
export const SUPPORTED_CURRENCIES = [
  'USD',
  'EUR',
  'GBP',
  'CAD',
  'AUD',
  'JPY',
  'CHF',
  'CNY',
  'SEK',
  'NOK',
  'DKK',
  'PLN',
  'CZK',
  'HUF',
  'RUB',
  'BRL',
  'MXN',
  'KRW',
  'SGD',
  'HKD',
  'NZD',
  'ZAR',
  'INR',
  'THB',
  'MYR',
  'PHP',
  'IDR',
  'VND',
  'TRY',
  'ILS',
] as const
export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number]

/**
 * Validates if a price value is valid
 */
export function isValidPrice(price: unknown): price is number {
  if (typeof price !== 'number') {
    return false
  }
  return !isNaN(price) && isFinite(price) && price > 0
}

/**
 * Validates if a currency is supported
 */
export function isValidCurrency(
  currency: unknown
): currency is SupportedCurrency {
  return (
    typeof currency === 'string' &&
    SUPPORTED_CURRENCIES.includes(currency as SupportedCurrency)
  )
}

/**
 * Safely parses price from various input types
 */
export function parsePrice(input: unknown): number | null {
  if (typeof input === 'number') {
    return isValidPrice(input) ? input : null
  }

  if (typeof input === 'string') {
    // Remove common currency symbols and formatting
    const cleanInput = input.replace(/[^\d.-]/g, '')
    const parsed = parseFloat(cleanInput)
    return isValidPrice(parsed) ? parsed : null
  }

  return null
}

/**
 * Safely parses currency, defaulting to USD if invalid
 */
export function parseCurrency(input: unknown): SupportedCurrency {
  if (isValidCurrency(input)) {
    return input
  }
  return 'USD'
}

/**
 * Creates a validated pricing object
 */
export function createPricingData(
  price: unknown,
  currency: unknown = 'USD',
  options: { offerPrice?: unknown; isParsed?: boolean } = {}
): PricingData | null {
  const validPrice = parsePrice(price)
  if (!validPrice) {
    return null
  }

  const validCurrency = parseCurrency(currency)
  const validOfferPrice = options.offerPrice
    ? parsePrice(options.offerPrice)
    : undefined

  return {
    retailPrice: validPrice,
    currency: validCurrency,
    ...(validOfferPrice && { offerPrice: validOfferPrice }),
    ...(options.isParsed !== undefined && { isParsed: options.isParsed }),
  }
}

/**
 * Creates validated dataDetected pricing
 */
export function createDataDetectedPricing(
  price: unknown,
  currency: unknown = 'USD',
  additionalData: Omit<DataDetectedPricing, 'price' | 'currency'> = {}
): DataDetectedPricing | null {
  const validPrice = parsePrice(price)
  if (!validPrice) {
    return null
  }

  const validCurrency = parseCurrency(currency)

  return {
    price: validPrice,
    currency: validCurrency,
    ...additionalData,
  }
}

/**
 * Ensures pricing and dataDetected stay in sync
 */
export function syncPricingData(
  pricing: PricingData,
  existingDataDetected: any = {}
): DataDetectedPricing {
  return {
    ...existingDataDetected,
    price: pricing.retailPrice,
    currency: pricing.currency,
  }
}

interface PriceCandidate {
  value: unknown
  priority: number
  path: string
  requiresManualEntry?: boolean
}

/**
 * Recursively finds all potential price values in a nested object structure
 */
function findAllPriceCandidates(
  obj: any,
  basePath = '',
  visited = new Set()
): PriceCandidate[] {
  if (!obj || typeof obj !== 'object' || visited.has(obj)) {
    return []
  }

  visited.add(obj)
  const candidates: PriceCandidate[] = []

  // Define priority scoring for different price field types
  const getPriority = (key: string, parentPath: string): number => {
    // Higher number = higher priority
    if (key === 'offerPrice') return 100 // Actual selling price
    if (key === 'retailPrice') return 90 // Standard retail price
    if (key === 'price' && parentPath.includes('pricing')) return 80 // Price in pricing object
    if (key === 'price') return 70 // Generic price field
    return 60 // Other price-like fields
  }

  // Check current level for price fields
  for (const [key, value] of Object.entries(obj)) {
    const currentPath = basePath ? `${basePath}.${key}` : key

    // Check if this looks like a price field
    if (['price', 'retailPrice', 'offerPrice', 'regularPrice'].includes(key)) {
      const requiresManualEntry = obj.requiresManualEntry === true
      candidates.push({
        value,
        priority: getPriority(key, basePath),
        path: currentPath,
        requiresManualEntry,
      })
    }

    // Recursively search nested objects
    if (typeof value === 'object' && value !== null) {
      candidates.push(...findAllPriceCandidates(value, currentPath, visited))
    }
  }

  return candidates
}

/**
 * Extracts price from item with intelligent recursive search
 * Handles deeply nested structures and multiple dataDetected levels
 */
export function extractItemPrice(item: any): number | null {
  if (!item || typeof item !== 'object') {
    return null
  }

  // Find all potential price candidates in the entire structure
  const allCandidates = findAllPriceCandidates(item)

  // Filter out candidates that require manual entry (they indicate no price was found)
  const validCandidates = allCandidates.filter(
    (candidate) => !candidate.requiresManualEntry
  )

  if (validCandidates.length === 0) {
    // If no valid candidates, fall back to checking manual entry candidates
    // in case that's all we have
    const manualCandidates = allCandidates.filter(
      (candidate) => candidate.requiresManualEntry
    )
    for (const candidate of manualCandidates.sort(
      (a, b) => b.priority - a.priority
    )) {
      const parsed = parsePrice(candidate.value)
      if (parsed !== null && parsed <= 2147483647) {
        // PostgreSQL integer limit
        return parsed
      }
    }
    return null
  }

  // Sort by priority (highest first) and try to parse each candidate
  validCandidates.sort((a, b) => b.priority - a.priority)

  for (const candidate of validCandidates) {
    const parsed = parsePrice(candidate.value)
    if (parsed !== null && parsed <= 2147483647) {
      // PostgreSQL integer limit
      return parsed
    }
  }

  return null
}

/**
 * Maps common currency symbols to their currency codes
 */
export const getSymbolToCurrencyMap = (): Record<string, string> => ({
  $: 'USD',
  '€': 'EUR',
  '£': 'GBP',
  '¥': 'JPY',
  kr: 'DKK', // Danish Krone
  C$: 'CAD',
  A$: 'AUD',
})

/**
 * Comprehensive mapping of currency symbols to ISO 4217 currency codes
 * Used for normalizing currency input from various sources
 */
export const CURRENCY_SYMBOL_TO_CODE: Record<string, string> = {
  // Major currencies
  $: 'USD',
  '€': 'EUR',
  '£': 'GBP',
  '¥': 'JPY',
  '￥': 'CNY', // Chinese Yuan (full-width)
  '₹': 'INR',
  '₩': 'KRW',
  '₽': 'RUB',
  '₺': 'TRY',
  '₪': 'ILS',
  '฿': 'THB',
  '₫': 'VND',
  '₱': 'PHP',
  R$: 'BRL',
  // Dollars with prefix
  US$: 'USD',
  C$: 'CAD',
  CA$: 'CAD',
  A$: 'AUD',
  AU$: 'AUD',
  NZ$: 'NZD',
  HK$: 'HKD',
  S$: 'SGD',
  SG$: 'SGD',
  MX$: 'MXN',
  // Scandinavian currencies
  kr: 'SEK', // Default to SEK for kr
  'kr.': 'SEK',
  SEK: 'SEK',
  NOK: 'NOK',
  DKK: 'DKK',
  // Swiss Franc
  CHF: 'CHF',
  Fr: 'CHF',
  'Fr.': 'CHF',
  // Polish Zloty
  zł: 'PLN',
  PLN: 'PLN',
  // Czech Koruna
  Kč: 'CZK',
  CZK: 'CZK',
  // Hungarian Forint
  Ft: 'HUF',
  HUF: 'HUF',
  // Indonesian Rupiah
  Rp: 'IDR',
  IDR: 'IDR',
  // Malaysian Ringgit
  RM: 'MYR',
  MYR: 'MYR',
  // South African Rand
  R: 'ZAR',
  ZAR: 'ZAR',
}

/**
 * Validates if a currency code is valid for use with Intl.NumberFormat
 * This is the most reliable way to check if a currency code is valid
 */
export function isValidIntlCurrency(currency: string): boolean {
  if (!currency || typeof currency !== 'string') {
    return false
  }

  try {
    // Intl.NumberFormat will throw for invalid currency codes
    new Intl.NumberFormat('en-US', { style: 'currency', currency })
    return true
  } catch {
    return false
  }
}

/**
 * Normalizes a currency input (symbol or code) to a valid 3-letter ISO 4217 currency code
 * Returns undefined if the currency cannot be normalized to a valid code
 *
 * @param input - Currency symbol (e.g., "$", "€", "£") or code (e.g., "USD", "EUR")
 * @returns Valid 3-letter currency code or undefined if invalid
 */
export function normalizeCurrency(input: unknown): string | undefined {
  if (!input || typeof input !== 'string') {
    return undefined
  }

  const trimmed = input.trim()
  if (!trimmed) {
    return undefined
  }

  // Check if it's already a valid 3-letter currency code
  const upperTrimmed = trimmed.toUpperCase()
  if (trimmed.length === 3 && isValidIntlCurrency(upperTrimmed)) {
    return upperTrimmed
  }

  // Try to map from symbol to code
  // First try exact match
  if (CURRENCY_SYMBOL_TO_CODE[trimmed]) {
    const code = CURRENCY_SYMBOL_TO_CODE[trimmed]
    if (isValidIntlCurrency(code)) {
      return code
    }
  }

  // Try uppercase match for codes like "usd" -> "USD"
  if (CURRENCY_SYMBOL_TO_CODE[upperTrimmed]) {
    const code = CURRENCY_SYMBOL_TO_CODE[upperTrimmed]
    if (isValidIntlCurrency(code)) {
      return code
    }
  }

  // If the input looks like a 3-letter code but isn't in our map,
  // validate it directly with Intl
  if (trimmed.length === 3 && /^[A-Za-z]{3}$/.test(trimmed)) {
    if (isValidIntlCurrency(upperTrimmed)) {
      return upperTrimmed
    }
  }

  return undefined
}

/**
 * Helper to extract currency symbol/code from price string
 */
export const extractCurrency = (priceStr: string): string => {
  if (!priceStr || typeof priceStr !== 'string') return 'USD' // Default to USD
  const match = priceStr.match(/^[^\d\s.,]+/)
  const extracted = match ? match[0].trim() : 'USD'

  // If it's a 3-letter currency code, return it if supported
  if (
    extracted.length === 3 &&
    SUPPORTED_CURRENCIES.includes(extracted as SupportedCurrency)
  ) {
    return extracted
  }

  // Map common currency symbols to codes
  const symbolToCurrency = getSymbolToCurrencyMap()

  return symbolToCurrency[extracted] || 'USD'
}

/**
 * Helper to extract numeric price from price string
 */
export const extractPrice = (priceStr: string): number | null => {
  if (!priceStr || typeof priceStr !== 'string') return null

  // Match numbers with optional decimal points, handling commas
  const match = priceStr.match(/[\d,]+\.?\d*/)
  if (!match) return null

  // Remove commas and convert to number
  return parseFloat(match[0].replace(/,/g, ''))
}

/**
 * Extract both currency and price from a price string like "DKK 7,295*"
 */
export function extractPriceAndCurrency(priceStr: string): {
  price: number | null
  currency: SupportedCurrency
} {
  const currency = parseCurrency(extractCurrency(priceStr))
  const price = extractPrice(priceStr)

  return { price, currency }
}

/**
 * Formats price for display using currency-symbol-map for comprehensive symbol support
 */
export function formatPrice(
  price: number,
  currency: string | SupportedCurrency = 'USD',
  options: {
    minimumFractionDigits?: number
    maximumFractionDigits?: number
    showCurrencySymbol?: boolean
  } = {}
): string {
  const {
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
    showCurrencySymbol = true,
  } = options

  const formatted = price.toLocaleString('en-US', {
    minimumFractionDigits,
    maximumFractionDigits,
  })

  if (!showCurrencySymbol) {
    return formatted
  }

  // Use currency-symbol-map for comprehensive currency symbol support
  const symbol = getSymbolFromCurrency(currency) || currency
  return `${symbol}${formatted}`
}

/** Return the display symbol for a currency code (e.g. 'GBP' → '£',
 *  'EUR' → '€', 'JPY' → '¥', 'USD' → '$'). Uses `currency-symbol-map`
 *  for comprehensive ISO 4217 coverage. Falls back to `$` when the
 *  code is unknown / missing — matches the pre-localization default
 *  used elsewhere in the codebase.
 *
 *  Use this whenever you need just the symbol for prompt formatting
 *  / inline display, separately from a full `formatPrice` call. The
 *  brain's ranker prompts call this to keep Haiku from copying the
 *  wrong currency into its rationale when the grid is GBP / EUR. */
export function currencySymbol(code: string | undefined): string {
  if (typeof code !== 'string' || code.trim().length === 0) return '$'
  return getSymbolFromCurrency(code.trim().toUpperCase()) || '$'
}

/**
 * Is it PROVEN that two prices are in the same currency?
 *
 * Returns true only when both inputs are known AND normalise to the same ISO
 * code. Anything else — null, undefined, empty, or a symbol/code we cannot map —
 * returns FALSE, meaning "not provably the same". Unknown never reads as a match.
 *
 * That default is the whole point. Cross-currency arithmetic has been shipped
 * repeatedly in this repo (savings badges, "% less", typical-price deltas), and it
 * fails silently: the number looks plausible and carries the wrong symbol. A
 * 569 AUD cart item against a USD 219 "typical" was reported to shoppers as
 * "overpriced" with maximum confidence.
 *
 * Use this to GATE the arithmetic, then abstain — do not convert. There is no FX
 * rate source in this codebase, and a converted figure would carry its own
 * staleness error while still not fixing like-for-like mismatches (a rug's price
 * scales with size; a 2ct diamond cannot be compared to a lookalike).
 *
 * Prior art this consolidates, each hand-rolled with slightly different
 * semantics: `brain/verificationHook.ts` (abstains on unknown — the model for
 * this), `steals/catalogBrand.ts` (filters history to the product's currency),
 * `research-report/project-seo.ts`, and `research/reportVisuals` (dominant-
 * currency filters).
 *
 * NOTE a bare `$` normalises to USD though it could be AUD/CAD/NZD/SGD. This
 * function cannot detect that ambiguity — callers that care should treat a
 * symbol-derived code as weaker evidence than an explicit ISO code.
 */
export function sameCurrency(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  const left = normalizeCurrency(a)
  const right = normalizeCurrency(b)
  // Unmappable on either side → not provably the same.
  if (!left || !right) return false
  return left === right
}

/**
 * Detect the ISO currency code from a PRICE-LIKE string, e.g. `"$149.77"`,
 * `"A$349"`, `"149,77 €"`, `"DKK 7,295"`. Returns undefined when nothing is
 * detectable — never a default.
 *
 * Why this exists rather than reusing `extractCurrency`: that helper is anchored
 * to the start of the string and consults a 7-entry map, so it returns USD for
 * `CA$349`, `NZ$99`, `AU$349`, `R$99` and every suffix format like `"149,77 €"`.
 * Silently-wrong USD is exactly the failure this is meant to prevent.
 *
 * Matches the LONGEST symbol first, which is the detail a naive char-by-char scan
 * gets wrong: iterating `"A$349"` finds `$` before it ever considers `A$`, giving
 * USD for an Australian price.
 *
 * Intended for short price strings, NOT arbitrary prose: the ISO-code branch
 * matches any 3-letter word, and a few English words are real currency codes
 * (`ALL` = Albanian lek, `TRY` = Turkish lira). Fine for `typical_prices` fields;
 * don't point it at a product title or description.
 */
export function detectCurrencyInPriceString(
  input: string | null | undefined
): string | undefined {
  if (!input || typeof input !== 'string') return undefined
  const trimmed = input.trim()
  if (!trimmed) return undefined

  // An explicit ISO code anywhere wins — least ambiguous signal available.
  const code = trimmed.match(/\b([A-Za-z]{3})\b/)
  if (code) {
    const normalized = normalizeCurrency(code[1])
    if (normalized) return normalized
  }

  // Longest symbol first so `CA$`/`A$`/`US$` beat a bare `$`.
  const symbols = Object.keys(CURRENCY_SYMBOL_TO_CODE).sort(
    (a, b) => b.length - a.length
  )
  for (const symbol of symbols) {
    if (trimmed.includes(symbol)) {
      const normalized = normalizeCurrency(symbol)
      if (normalized) return normalized
    }
  }
  return undefined
}

/**
 * Parse a price string to a number ONLY when the format is unambiguous;
 * abstain otherwise.
 *
 * ⛔ Do NOT use `extractPrice` for this. It is not locale-aware and is
 * catastrophically wrong on ordinary European formats — measured, not
 * theorised:
 *
 *   extractPrice('€24,99')   -> 2499      (100x high — comma read as grouping)
 *   extractPrice('1 234 zł') -> 1         (1234x low — space ends the match)
 *   extractPrice('1.234,56') -> 1.234     (1000x low)
 *
 * A wrong price is far worse than no price. It does not throw and it does not
 * look broken — it produces a plausible number that downstream code then
 * compares, exactly the failure class
 * `docs/adr/0006-no-cross-currency-arithmetic-unknown-abstains.md` exists for.
 * So this returns `null` rather than guessing whenever the grouping and decimal
 * separators cannot be told apart.
 *
 * Accepted, after stripping everything that is not a digit, `.` or `,`
 * (which also removes symbols, ISO codes and any thousands-spacing):
 *
 *   1234        1234        plain integer
 *   24.99       24.99       dot decimal
 *   1,234.56    1234.56     comma grouping + dot decimal
 *   1,299       1299        comma grouping alone (3 digits after the comma)
 *   24,99       24.99       comma decimal (1-2 digits after the comma)
 *   1.234,56    1234.56     dot grouping + comma decimal
 *
 * ⚠️ Deliberately NOT accepted: `1.234`. It is 1234 under European grouping and
 * 1.234 under a three-decimal price, with nothing in the string to separate the
 * two readings. Abstaining costs a rare valid parse; guessing costs a 1000x
 * error on a number a shopper is shown.
 */
export function parseUnambiguousPrice(input: unknown): number | null {
  if (typeof input === 'number') {
    return Number.isFinite(input) && input > 0 ? input : null
  }
  if (typeof input !== 'string') return null

  // ⚠️ Strip the minus BEFORE the symbol strip, not after: removing everything
  // that is not a digit/`.`/`,` would silently turn "-5" into "5" — a negative
  // price reappearing as a positive one.
  if (/^\s*[^\d]*-/.test(input)) return null

  const numeric = input.replace(/[^\d.,]/g, '')
  if (!numeric) return null

  let normalized: string | null = null
  if (/^\d+$/.test(numeric)) {
    normalized = numeric
  } else if (/^\d+\.\d{1,2}$/.test(numeric)) {
    normalized = numeric
  } else if (/^\d{1,3}(,\d{3})+(\.\d{1,2})?$/.test(numeric)) {
    normalized = numeric.replace(/,/g, '')
  } else if (/^\d+,\d{1,2}$/.test(numeric)) {
    normalized = numeric.replace(',', '.')
  } else if (/^\d{1,3}(\.\d{3})+,\d{1,2}$/.test(numeric)) {
    normalized = numeric.replace(/\./g, '').replace(',', '.')
  }
  if (normalized === null) return null

  const value = Number.parseFloat(normalized)
  return Number.isFinite(value) && value > 0 ? value : null
}
